import {
  Badge,
  ComponentMultiStyleConfig,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Text,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useEffect, useMemo, useState } from "react";
import {
  ComponentStyleProps,
  componentStylePropsOf,
} from "../../../../lib/styles/props/component-style-props";
import { isNullish } from "../../../../lib/util/common-util";
import { ChangeBadgeProperties } from "../../../../lib";

export type CMFormChangeBadgeProps<T extends {}> = ChangeBadgeProperties<T> & {
  /**
   * 値を、表示値に変換する関数。
   */
  toDisplayValue: (value: T) => string | Promise<string>;
} & ComponentStyleProps;

export const CMFormChangeBadge = <T extends {}>({
  size = "md",

  label,
  changedFromLabel,

  changedFrom,

  toDisplayValue,

  sx,
  ...rest
}: CMFormChangeBadgeProps<T>) => {
  const styles = useMultiStyleConfig("CMFormChangeBadgeStyles", { size });

  const [displayValue, setDisplayValue] = useState("");

  useEffect(() => {
    (async () => {
      if (isNullish(changedFrom)) {
        setDisplayValue("");
        return;
      }
      const result = toDisplayValue(changedFrom);
      if (typeof result === "string") {
        setDisplayValue(result);
        return;
      }
      setDisplayValue(await result);
    })();
  }, [toDisplayValue, changedFrom]);

  const badgeStyles = useMemo(() => {
    return {
      ...styles.badge,
      ...sx,
    };
  }, [styles.badge, sx]);

  return (
    <Popover trigger="hover">
      <PopoverTrigger>
        <Badge sx={badgeStyles} {...rest}>
          {label}
        </Badge>
      </PopoverTrigger>
      <PopoverContent sx={styles.popoverContent}>
        <PopoverHeader sx={styles.popoverHeader}>
          {changedFromLabel}:
        </PopoverHeader>
        <PopoverBody sx={styles.popoverBody}>
          <Text sx={styles.popoverBodyText}>{displayValue}</Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
};

export const CMFormChangeBadgeStyles: ComponentMultiStyleConfig = {
  parts: [
    "badge",
    "popoverContent",
    "popoverHeader",
    "popoverBody",
    "popoverBodyText",
  ],
  baseStyle: {
    badge: {
      borderWidth: "1px",
      borderColor: "gray.500",
    },
    popoverContent: {
      maxW: "max-content",

      overflow: "hidden",
    },
    popoverHeader: {
      px: "6px",
      pt: "4px",
      pb: "0",

      border: "none",
      fontSize: "xs",
      fontWeight: "bold",
    },
    popoverBody: {
      px: "6px",
      pt: "0",
      pb: "4px",

      overflowY: "auto",
    },
    popoverBodyText: {
      maxH: "200px",

      fontSize: "sm",
    },
  },
  sizes: {
    md: {
      badge: {
        fontSize: "xs",
      },
    },
    lg: {
      badge: {
        fontSize: "sm",
      },
    },
  },
};
